/**
 * Font Awesome to Phosphor Icons Mapping
 *
 * This file maps Font Awesome icon names to their Phosphor equivalents.
 * The mapping supports different Font Awesome weights (solid, regular, light)
 * and maps them to Phosphor weights (regular, bold, thin, light, fill, duotone).
 *
 * Structure:
 * - Key: Font Awesome icon name (without 'fa-' prefix)
 * - Value: Phosphor icon component name
 */

import * as PhosphorIcons from '@phosphor-icons/react';

export type PhosphorIconName = keyof typeof PhosphorIcons;

/**
 * Mapping from Font Awesome icon names to Phosphor icon names
 */
export const faToPhosphorMapping: Record<string, PhosphorIconName> = {
	// Navigation & UI Controls
	'arrow-up': 'ArrowUp',
	'arrow-down': 'ArrowDown',
	'arrow-left': 'ArrowLeft',
	'arrow-right': 'ArrowRight',
	'caret-up': 'CaretUp',
	'caret-down': 'CaretDown',
	'caret-left': 'CaretLeft',
	'caret-right': 'CaretRight',
	'chevron-left': 'CaretLeft',
	'chevron-right': 'CaretRight',
	'chevron-up': 'CaretUp',
	'chevron-down': 'CaretDown',
	'chevron-circle-left': 'CaretCircleLeft',

	// Actions
	times: 'X',
	xmark: 'X',
	'times-circle': 'XCircle',
	plus: 'Plus',
	'plus-circle': 'PlusCircle',
	minus: 'Minus',
	check: 'Check',
	'check-circle': 'CheckCircle',
	'circle-check': 'CheckCircle',
	'check-square': 'CheckSquare',
	edit: 'PencilSimple',
	copy: 'Copy',
	eraser: 'Eraser',
	magic: 'MagicWand',
	'rotate-left': 'ArrowCounterClockwise',
	trash: 'Trash',
	download: 'Download',
	upload: 'Upload',
	'share-alt': 'ShareNetwork',
	'share-all': 'Share',
	filter: 'Funnel',
	search: 'MagnifyingGlass',

	// Informational
	'info-circle': 'Info',
	'circle-info': 'Info',
	'exclamation-circle': 'Warning',
	'exclamation-triangle': 'WarningCircle',
	'question-circle': 'Question',

	// Files & Documents
	file: 'File',
	'files-o': 'Files',
	'file-archive-o': 'FileArchive',
	'file-audio-o': 'FileAudio',
	'file-code-o': 'FileCode',
	'file-excel-o': 'FileXls',
	'file-powerpoint-o': 'FilePpt',
	'file-text-o': 'FileText',
	'file-video-o': 'FileVideo',
	'file-contract': 'FileText',
	paperclip: 'Paperclip',
	book: 'Book',

	// Status & Indicators
	clock: 'Clock',
	'clock-o': 'Clock',
	'clock-rotate-left': 'ClockCounterClockwise',
	circle: 'Circle',
	'dot-circle': 'DotOutline',
	'circle-o': 'Circle',
	square: 'Square',
	spinner: 'CircleNotch',
	bomb: 'Fire',

	// Security & Visibility
	lock: 'Lock',
	'unlock-alt': 'LockOpen',
	eye: 'Eye',
	'eye-slash': 'EyeSlash',

	// Locations & Navigation
	'map-marker-alt': 'MapPin',
	'globe-africa': 'Globe',
	'external-link': 'ArrowSquareUpRight',
	'external-link-square': 'ArrowSquareUpRight',

	// User & Social
	'user-secret': 'Detective',

	'birthday-cake': 'Cake',
	star: 'Star',
	award: 'Medal',
	trophy: 'Trophy',

	// Media
	'video-camera': 'VideoCamera',

	// UI Elements
	bars: 'List',
	'grip-lines': 'DotsSix',
	microphone: 'Microphone',
	code: 'Code',
	sitemap: 'TreeStructure',
	'project-diagram': 'TreeStructure',
	'list-ul': 'ListBullets',
	cog: 'Gear',
	'sign-out': 'SignOut',
	'drafting-compass': 'Compass',
	marker: 'MapPin',
	calculator: 'Calculator',
	leaf: 'Leaf',
	'compress-arrows-alt': 'ArrowsIn',
	'expand-arrows-alt': 'ArrowsOut',

	// Menu Icons
	home: 'House',
	'sticky-note': 'Note',
	building: 'BuildingOffice',
	'chart-pie': 'ChartPieSlice',
	'chart-line': 'ChartLine',
	cube: 'Cube',
	archive: 'Archive',
	'tasks-alt': 'ListChecks',
	landmark: 'Bank',
	'address-card': 'IdentificationCard',
	users: 'Users',
	envelope: 'Envelope',

	'calendar-alt': 'CalendarDots',
	'file-alt': 'File',
	pencil: 'Pencil',
	tasks: 'ListChecks',
	'file-pdf-o': 'FilePdf',
	'file-word-o': 'FileDoc',
	'file-image-o': 'FileImage',
	'file-o': 'File',
	'file-upload': 'Upload',
	'user-friends': 'Users',
	'graduation-cap': 'GraduationCap',
	briefcase: 'Briefcase',
};

/**
 * Font Awesome modifier to Phosphor weight mapping
 */
export const faModifierToPhosphorWeight: Record<string, PhosphorIcons.IconWeight> = {
	fa: 'fill', // Default FA (typically solid) -> Phosphor Fill
	fas: 'fill', // Font Awesome Solid -> Phosphor Fill
	far: 'regular', // Font Awesome Regular -> Phosphor Regular
	fal: 'thin', // Font Awesome Light -> Phosphor Thin
	fab: 'bold', // Font Awesome Brands -> Phosphor Bold (best approximation)
	'fa-light': 'light', // explicit Phosphor Light weight
};

/**
 * Get Phosphor icon component from Font Awesome icon name
 */
export function getPhosphorIcon(faIconName: string): PhosphorIconName | null {
	// Remove any 'fa-' prefix if present
	const cleanIconName = faIconName.replace(/^fa-/, '');
	return faToPhosphorMapping[cleanIconName] || null;
}

/**
 * Get Phosphor weight from Font Awesome modifier
 */
export function getPhosphorWeight(faModifier?: string): PhosphorIcons.IconWeight {
	if (!faModifier) return 'fill'; // Default to fill to match FA solid style

	// Extract the FA modifier from a string like 'far color-red'
	const modifierParts = faModifier.split(' ');
	const faPrefix = modifierParts.find((part) => part.startsWith('fa'));

	return faModifierToPhosphorWeight[faPrefix || 'fa'] || 'fill';
}

/**
 * Get additional CSS classes from Font Awesome modifier
 * (e.g., color classes, animation classes)
 */
export function getAdditionalClasses(faModifier?: string): string {
	if (!faModifier) return '';

	// Filter out FA prefixes and fa-pulse (handled via icon-pulse), keep classes like 'color-red'
	return faModifier
		.split(' ')
		.filter((part) => !part.match(/^fa[rsbl]?$/) && part !== 'fa-pulse')
		.join(' ');
}
